import React, { useMemo, useCallback } from "react";
import { MDSSessionPart } from "@/mds/components/sessions/session-parts/session-part";
import { extractLatestThinkingTextFromSessionItem } from "./reasoning-group.utils";
import { ReasoningGroupHeader } from "./reasoning-group-header";
import {
  SessionItem,
  SessionRenderingContext,
} from "@/mds/components/sessions/session-item/session-item.utils";
import { MDSBubble, MDSBubbleAlignment, MDSBubbleVariant } from "@/mds/components/bubble";
import styled from "@emotion/styled";
import { resolveSessionPartId } from "@/mds/components/sessions/session-context/slices/events/mds-session-context.events.utils";
import {
  useIsFirstItemInSessionGroup,
  useIsLastItemInSessionGroup,
  useIsLastItemInSessionHistory,
  useMDSSession,
} from "@/mds/components/sessions/session-context";
import { SessionPartKind, SessionStatus } from "@/mds/components/sessions/session.types";

interface ReasoningGroupSessionItemRendererProps {
  item: SessionItem;
  context: SessionRenderingContext;
}

/**
 * Renders a collapsible thought process section that shows reasoning parts.
 * Auto-collapses when processing starts to show current thinking as header text.
 */
export const ReasoningGroupSessionItemRenderer: React.FC<
  ReasoningGroupSessionItemRendererProps
> = ({ item, context }) => {
  const { toggleSessionItemExpandedState } = useMDSSession();

  const itemData = item.data;

  const isLastInSessionHistory = useIsLastItemInSessionHistory({ itemId: item.id });

  const isProcessing = useMemo(() => {
    if (!isLastInSessionHistory) {
      return false;
    }

    return context.status === SessionStatus.Processing;
  }, [context.status, isLastInSessionHistory]);

  const isInInterruptedGroup = useMemo(() => {
    return itemData.some(data => data.part.kind === SessionPartKind.InterruptV1);
  }, [itemData]);

  const latestThinkingText = extractLatestThinkingTextFromSessionItem({ item });

  const isCollapsed = useMemo(() => !item.context.isExpanded, [item.context.isExpanded]);

  const hasMultipleParts = useMemo(() => itemData.length > 1, [itemData.length]);

  const isExpandable = useMemo(() => {
    return hasMultipleParts;
  }, [hasMultipleParts]);

  const handleHeaderClick = useCallback(() => {
    if (!isExpandable) {
      return;
    }

    toggleSessionItemExpandedState({ itemId: item.id });
  }, [item.id, isExpandable, toggleSessionItemExpandedState]);

  const isFirstInGroup = useIsFirstItemInSessionGroup({ itemId: item.id });
  const isLastInGroup = useIsLastItemInSessionGroup({ itemId: item.id });

  return (
    <Bubble
      variant={MDSBubbleVariant.Transparent}
      align={MDSBubbleAlignment.Left}
      isLastInGroup={isLastInGroup}
    >
      <ReasoningGroupHeader
        latestThinkingText={latestThinkingText}
        isCollapsed={isCollapsed}
        isExpandable={isExpandable}
        onClick={handleHeaderClick}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
        isProcessing={isProcessing}
        itemCount={itemData.length}
      />

      {!isCollapsed && (
        <>
          {itemData.map(data => {
            const partId = data.context.partId ?? resolveSessionPartId({ part: data.part });
            const partConfig = context.config;
            const partContext = {
              ...data.context,
              isInInterruptedGroup: isInInterruptedGroup,
            };

            return (
              <MDSSessionPart
                key={partId}
                part={data.part}
                context={partContext}
                config={partConfig}
              />
            );
          })}
        </>
      )}
    </Bubble>
  );
};

const Bubble = styled(MDSBubble)`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing["0.5"]};
`;
